import { useCallback, useState } from 'react';

import AutoPostSection from '../components/AutoPostSection';
import PromptsSection from '../components/PromptsSection';
import ScanSection from './ScanSection';
import SourcesSection from './SourcesSection';

/**
 * Setup: the switch, the scan, the watchlist and the reply prompt, on one page.
 *
 * The sections read in the order the work happens. Auto post comes first because it is the only
 * control on the page that decides whether any of the work below ever leaves the building: with
 * it on, the local poster sends what the guardrails allow, and with it off the replies wait for a
 * human. The scan is what goes and finds the posts; the watchlist is who the engine looks at; the
 * prompt is what a reply says and how it says it. There used to be a fifth section of "controls" -
 * modes, a kill switch, caps - and it was deleted because every reply was pasted by hand, so there
 * was never a mode to choose and nothing for a cap to gate. 7 Oct 2026 brought back the three that
 * gate a machine, under Auto post, once a machine could post. The "Your sign-in" card went the
 * same way when the login became a static pair on the Worker: there is no password to set from in
 * here any more (see pages/Login.tsx).
 *
 * The scan is second because it is where a session starts - it is the button that produces the
 * drafts everything below describes.
 *
 * ## The watchlist is read by a section above it, so it has to say when it moves
 *
 * The Scan panel derives what each lane will search for from the *watchlist* rows it can see: the
 * chips are the enabled Instagram hashtags and Facebook group URLs, and "Scan all (N)" counts the
 * lanes that have something to search for at all. So enabling a community is not a change that
 * stays in the Watchlist section - it restates the panel above it.
 *
 * `nestmatch.sources` is not in the realtime publication, so there is no subscription to lean on
 * here. `watchlistVersion` below is the wire instead: SourcesSection calls `onWatchlistChanged`
 * once a write has landed, and every section that read the watchlist against that counter reloads.
 * A counter rather than a boolean, so two changes in a row cannot collapse into one edge.
 *
 * Deliberately *not* solved by lifting `sources` into this component: the two sections want
 * different columns in different orders, and a shared fetch would only give them a shared bug.
 */
export default function Setup({ actor }: { actor: string }) {
  /** Bumped by the watchlist when a row is written; read by the sections that display its effects. */
  const [watchlistVersion, setWatchlistVersion] = useState(0);
  const watchlistChanged = useCallback(() => setWatchlistVersion((current) => current + 1), []);

  return (
    <>
      <h2>Setup</h2>
      <p className="muted small">
        How replies go out, what the engine watches, what a reply may say, and how much runs. Every
        change here takes effect immediately.
      </p>

      <AutoPostSection actor={actor} />

      <ScanSection actor={actor} watchlistVersion={watchlistVersion} />

      <SourcesSection actor={actor} onWatchlistChanged={watchlistChanged} />

      <PromptsSection actor={actor} />
    </>
  );
}
